import {Component, CUSTOM_ELEMENTS_SCHEMA, inject, Inject, OnDestroy, OnInit, QueryList, ViewChildren} from '@angular/core';
import {combineLatest, iif, Observable, of, ReplaySubject, Subject, Subscription, switchMap, tap} from 'rxjs';
import {DeliveryProduct} from '../../shared/model/delivery-product';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {ErrorHandlingService} from '../../shared/service/error-handling/error-handling.service';
import {ErrorInfo} from '../../shared/model/error-info';
import {environment} from '../../../../environments/environment';
import {DatePipe, NgClass, AsyncPipe, NgTemplateOutlet} from '@angular/common';
import {DeliveryService} from '../../shared/service/delivery/delivery.service';
import {DeliveryDescription} from '../../shared/model/delivery-description';
import {AdobeTrackingService} from '@datev/ngx-adobe-tracking';
import {Platform} from '@angular/cdk/platform';
import {PersonalService} from '../../shared/service/personal/personal.service';
import {AppStateService, DocumentStoreService} from 'commons';
import {catchError, map} from 'rxjs/operators';
import {YesNoPipe} from '../../shared/pipes/yes-no.pipe';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {ErrorBoxComponent} from '../error-box/error-box.component';
import {MatCard} from '@angular/material/card';
import {DeliveryBasesComponent} from '../delivery-bases/delivery-bases.component';
import {ChangeCategoriesComponent} from '../change-categories/change-categories.component';
import {
  DacklEmptyStateComponent,
  DacklBreakpointObserver,
  DacklBreakpoint
} from '@datev/ngx-dackl';
import {MatIcon} from '@angular/material/icon';
import {MatButton, MatFabButton} from '@angular/material/button';
import {MatSlideToggle, MatSlideToggleChange} from '@angular/material/slide-toggle';
import {FormsModule} from '@angular/forms';
import {PageState, PageStateService} from '../../shared/service/page-state/page-state.service';
import {DeliveryType} from '../../shared/model/delivery-type';
import {MatTab, MatTabGroup} from '@angular/material/tabs';
import {DeliveryProductType} from '../../shared/model/delivery-product-type';
import '@datev/dacks/content';
import '@datev/dacks/content-right-aside';
import '@datev/doc-viewer';
import {HtmlViewerComponent} from '../html-viewer/html-viewer.component';
import {DeliveryProductsTableViewComponent} from '../delivery-products-table-view/delivery-products-table-view.component';
import { FooterSpacing } from "../footer/footer-spacing";


interface DeliveryViewModel {
  description: DeliveryDescription;
  serviceReleasesAndFullVersion: DeliveryProduct[];
  hotfixes: DeliveryProduct[];
  allProducts: DeliveryProduct[];
}

export interface DeliveryPageState extends PageState {
  isPersonalFilter: boolean;
}

@Component({
  selector: 'myupdates-delivery-products-component',
  templateUrl: './delivery-products.component.html',
  styleUrls: ['./delivery-products.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [MatButton, MatIcon, ChangeCategoriesComponent, DeliveryBasesComponent, RouterLink,
    NgClass, MatCard, DacklEmptyStateComponent, ErrorBoxComponent, MatProgressSpinner, AsyncPipe,
    DatePipe, TranslatePipe, YesNoPipe, NgTemplateOutlet, MatSlideToggle, FormsModule, MatFabButton,
    MatTabGroup, MatTab, HtmlViewerComponent,
    DeliveryProductsTableViewComponent, FooterSpacing]
})
export class DeliveryProductsComponent implements OnInit, OnDestroy {

  @ViewChildren(DeliveryProductsTableViewComponent) tableViews?: QueryList<DeliveryProductsTableViewComponent>;

  deliveryViewModel$?: Observable<DeliveryViewModel>;
  errorInfo$ = new ReplaySubject<ErrorInfo>(1);
  isLegalNoticeShown = true;
  private routeSubscription?: Subscription;
  private personalizeSubscription?: Subscription;

  pageState = {} as DeliveryPageState;

  protected readonly DeliveryType = DeliveryType;
  pdfAdditionalInfoText = '';

  breakpoint$ = inject(DacklBreakpointObserver).breakpoint$;
  isLargeScreenWithRightAside$ = this.breakpoint$.pipe(map(breakpoint => breakpoint >= DacklBreakpoint.LG));
  isLargeScreenWithRightAside = false;

  selectedProduct: DeliveryProduct | null = null;

  detailsContentError$ = new Subject<boolean>();
  documentBuffer$?: Observable<{ buffer: ArrayBuffer, fileName: string } | null>;


  constructor(private readonly route: ActivatedRoute,
              private readonly router: Router,
              private readonly deliveryService: DeliveryService,
              private readonly errorHandlingService: ErrorHandlingService,
              private readonly adobeTrackingService: AdobeTrackingService,
              readonly appState: AppStateService,
              private readonly personalService: PersonalService,
              private readonly translate: TranslateService,
              private readonly datePipe: DatePipe,
              public platform: Platform,
              private readonly documentStoreService: DocumentStoreService,
              private readonly pageStateService: PageStateService,
              @Inject('Window') private readonly window: Window) {
  }

  ngOnInit(): void {
    this.pdfAdditionalInfoText = this.translate.instant('common.pdf_additional_info');
    this.isLargeScreenWithRightAside$.subscribe(isLargeScreenWithRightAside => {
      this.isLargeScreenWithRightAside = isLargeScreenWithRightAside;
      if (!isLargeScreenWithRightAside) {
        this.clearSelection();
      }
    });

    this.routeSubscription = this.route.paramMap.subscribe(paramMap => {
      const routeParamId = paramMap.get('id');

      if (routeParamId) {
        this.personalizeSubscription = this.appState.isPersonalized$.pipe(
          switchMap(isPersonalized =>
            iif(() => isPersonalized,
              combineLatest([this.personalService.activeBusinessPartner$, this.appState.isPersonalDeliveryFilter$],
                (_, isPersonalFilterActive) => isPersonalFilterActive),
              of(false))
          ),
          // Fehleranzeige: Zugriff auf Personalisierungsdaten
          catchError(err => {
            this.errorInfo$.next(this.errorHandlingService.toErrorInfo(err));
            return of();
          })
        ).subscribe(isPersonalizedView => {
          const delivery$ = this.deliveryService.getDeliveryById(routeParamId, isPersonalizedView).pipe(
            tap(delivery => this.validateDeliveryProductsDateInFuture(delivery.description.deliveryDate)),
            // Fehleranzeige: Zugriff auf Seitendaten (+ personalisierte Filter)
            catchError(err => {
              this.errorInfo$.next(this.errorHandlingService.toErrorInfo(err));
              return of();
            })
          );
          this.deliveryViewModel$ = delivery$.pipe(
            map(delivery => {
              const products = delivery.products ?? [];
              const serviceReleasesAndFullVersion = products.filter(
                p => p.productType !== DeliveryProductType.Hotfix
              );
              const hotfixes = products.filter(
                p => p.productType === DeliveryProductType.Hotfix
              );
              return {
                description: delivery.description,
                serviceReleasesAndFullVersion: serviceReleasesAndFullVersion,
                hotfixes: hotfixes,
                allProducts: products,
              };
            }));
        });
      } else {
        // kann eigentlich nicht eintreten, weil es die route products ohne id nicht gibt
        void this.router.navigate(['delivery']);
      }
    });

    this.pageState = this.pageStateService.pageState as DeliveryPageState;
    if (this.pageState.isPersonalFilter !== undefined) {
      this.appState.isPersonalDeliveryFilter = this.pageState.isPersonalFilter;
    } else {
      this.pageState.isPersonalFilter = this.appState.isPersonalDeliveryFilter;
    }

  }

  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
    this.personalizeSubscription?.unsubscribe();
 }

  selectProduct(product: DeliveryProduct): void {
    if (product.documentId || product.documentUrl) {
      this.selectedProduct = product;
      if (product.documentUrl) {
        this.documentBuffer$ = this.documentStoreService.getPdfBuffer(product.documentUrl).pipe(
          tap(() => this.detailsContentError$.next(false)),
          catchError(() => {
            this.detailsContentError$.next(true);
            return of(null);
          }),
          map(buffer => buffer ? { buffer, fileName: `${crypto.randomUUID()}.pdf` } : null)
        );
      }
    }
  }

  private clearSelection(): void {
    this.selectedProduct = null;
    this.documentBuffer$ = undefined;
  }

  onRightAsideCloseClick(): void {
    const productToFocus = this.selectedProduct;
    this.clearSelection();

    if (productToFocus?.id && this.tableViews) {
      setTimeout(() => {
        this.tableViews?.forEach(view =>
          view.focusProductLink(productToFocus.id)
        );
      });
    }
  }

  onTabChange(): void {
    this.clearSelection();
  }

  changePersonalFilterState(event: MatSlideToggleChange): void {
    const isPersonalFilter = event.checked;
    this.appState.isPersonalDeliveryFilter = isPersonalFilter;
    this.adobeTrackingService.publishClickEvent('UpdateTermineServiceHotfix:PersonalFilter:Toggle' +
      (isPersonalFilter ? 'Meine' : 'Alle'));
    this.pageState.isPersonalFilter = isPersonalFilter;
  }

  shareByMail(deliveryDescription: DeliveryDescription | null): void {

    if (!deliveryDescription) {
      return;
    }

    // Adobe Analytics Einbindung in erster Runde
    this.adobeTrackingService.publishClickEvent('Update-Termine:Ein Termin:Teilen');

    const formattedDate = this.datePipe.transform(deliveryDescription.deliveryDate, 'dd.MM.yyyy');
    const mailSubject = `Leseempfehlung \\ Update-Termin am ${formattedDate} \\ DATEV MyUpdates`;
    const mailBody = `Ich möchte Ihnen den folgenden Beitrag in DATEV MyUpdates empfehlen:` +
      `%0A${environment.entryPoint}delivery/products/${deliveryDescription.id}%0A%0A` +
      `Mit freundlichem Gruß`;

    this.window.location.href = `mailto:?subject=${mailSubject}&body=${mailBody}`;
  }

  navigateToProductDetails(product: DeliveryProduct, event?: KeyboardEvent): void {
    if (!event || event.code === 'Space' || event.code === 'Enter') {
      if ((product?.documentId || product?.documentUrl) && product?.id) {
        void this.router.navigate(['product', product.id], {relativeTo: this.route});
      }
    }
  }

  // Methode zum Anzeigen des Hinweises "Es kann zu Änderungen kommen"
  validateDeliveryProductsDateInFuture(deliveryDate: Date): void {
    this.isLegalNoticeShown = DeliveryService.validateDateIsInFuture(deliveryDate);
  }

}
